// src/pages/analytics/drill.ts
//
// Matrix drill-down (flag analytics.drill, agent 3.20.0+): a group row
// expands into its items / customers by re-running the same recipe with the
// child dimension plus the pane slicer that pins the parent row, on top of
// the current pane filters. Shared by GP Analysis and Overview (YoY).

export interface DrillTarget {
  /** Child dimension token sent to the recipe. */
  child: string;
  /** Plural noun for the UI ("items", "customers"). */
  childLabel: string;
  /** Pane slicer param that pins the parent row. */
  param: string;
  /** Row label → slicer value, when they differ. */
  value?: (label: string) => string;
}

const toCustomers = (param: string, value?: (label: string) => string): DrillTarget =>
  ({ child: 'customer', childLabel: 'customers', param, value });
const toItems: DrillTarget = { child: 'item', childLabel: 'items', param: 'fItemCategory' };
const toSubcatItems: DrillTarget = { child: 'item', childLabel: 'items', param: 'fItemSubcat' };

/** Drill target per parent dimension token (both tabs' tokens). */
export const DRILL: Record<string, DrillTarget> = {
  item_category:     toItems,
  category:          toItems,
  item_subcategory:  toSubcatItems,
  customer_category: toCustomers('fCustCategory'),
  price_level:       toCustomers('fPriceLevel', l => l.replace(/^Price /, '')),
  salesman:          toCustomers('fSalesman'),
  area:              toCustomers('fArea'),
};

/** Rows shown under an expanded parent (top N by sales). */
export const DRILL_ROWS = 25;

/** Labels a slicer can't match: '(none)', or a comma (slicers are comma-separated). */
export const canDrillLabel = (label: string) => label !== '(none)' && !label.includes(',');

/** Pane params for the child query of `label` under `target`. */
export const drillParams = (
  paneParams: Record<string, string | undefined>,
  target: DrillTarget,
  label: string,
): Record<string, string | undefined> =>
  ({ ...paneParams, [target.param]: target.value ? target.value(label) : label });
